import React, { useEffect, useState } from 'react';
import SectionHeader from './SectionHeader';
import NotesInput from './NotesInput';

const PERSONAL_PREFILL_FIELDS = [
  'first_name',
  'last_name',
  'phone',
  'email',
  'has_partner',
  'partner_first_name',
  'partner_last_name',
  'partner_phone',
  'partner_email',
  'uses_emoney',
];

function filterPersonalPrefill(prefill) {
  if (!prefill || typeof prefill !== 'object') {
    return {};
  }

  return PERSONAL_PREFILL_FIELDS.reduce((acc, field) => {
    const value = prefill[field];
    if (value !== undefined && value !== null && value !== '') {
      acc[field] = value;
    }
    return acc;
  }, {});
}

/**
 * Section 1 — Personal Information
 *
 * Verifies client contact info (with prefill where the advisor supplied it),
 * captures partner/spouse info if applicable, and asks two open-text
 * questions that help the advisor prepare for the meeting.
 *
 * @param {object} data     - Current section data.
 * @param {object} prefill  - Optional prefill values supplied by the advisor.
 * @param {function} onChange
 */
export default function StepPersonal({ data, prefill, onChange }) {
  const [hasMerged, setHasMerged] = useState(false);

  // Merge prefill values into the form on first render IF the form is empty
  useEffect(() => {
    if (hasMerged) return;
    if (!prefill) {
      setHasMerged(true);
      return;
    }
    const isEmpty = !data || Object.keys(data).length === 0;
    if (isEmpty) {
      onChange(filterPersonalPrefill(prefill));
    }
    setHasMerged(true);
  }, [prefill, data, hasMerged, onChange]);

  function update(field, value) {
    onChange({ ...(data || {}), [field]: value });
  }

  const hasPartner = data?.has_partner === 'yes';

  return (
    <div>
      <SectionHeader
        number={1}
        title="Personal Information"
        tip="Please verify your contact information below and answer a few
          quick questions so we know how to reach you and what to focus on
          during your review."
        showAnnualNote={false}
      />

      <div className="cashflow-section-card">
        <h6 className="cashflow-subsection-title">Your contact information</h6>
        <div className="row g-2">
          <div className="col-md-6">
            <label htmlFor="first_name" className="form-label">First name <span className="text-danger">*</span></label>
            <input
              id="first_name"
              type="text"
              className="form-control"
              value={data?.first_name || ''}
              onChange={(e) => update('first_name', e.target.value)}
            />
          </div>
          <div className="col-md-6">
            <label htmlFor="last_name" className="form-label">Last name <span className="text-danger">*</span></label>
            <input
              id="last_name"
              type="text"
              className="form-control"
              value={data?.last_name || ''}
              onChange={(e) => update('last_name', e.target.value)}
            />
          </div>
          <div className="col-md-6">
            <label htmlFor="phone" className="form-label">Best phone <span className="text-danger">*</span></label>
            <input
              id="phone"
              type="tel"
              className="form-control"
              value={data?.phone || ''}
              onChange={(e) => update('phone', e.target.value)}
            />
          </div>
          <div className="col-md-6">
            <label htmlFor="email" className="form-label">Best email <span className="text-danger">*</span></label>
            <input
              id="email"
              type="email"
              className="form-control"
              value={data?.email || ''}
              onChange={(e) => update('email', e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="cashflow-section-card">
        <h6 className="cashflow-subsection-title">Household</h6>
        <div className="mb-3">
          <label className="form-label">Do you share finances with a spouse or partner?</label>
          <div>
            {['no', 'yes'].map((opt) => (
              <div className="form-check form-check-inline" key={opt}>
                <input
                  className="form-check-input"
                  type="radio"
                  id={`has_partner_${opt}`}
                  name="has_partner"
                  checked={data?.has_partner === opt}
                  onChange={() => update('has_partner', opt)}
                />
                <label className="form-check-label" htmlFor={`has_partner_${opt}`}>
                  {opt === 'yes' ? 'Yes' : 'No'}
                </label>
              </div>
            ))}
          </div>
        </div>

        {hasPartner && (
          <div className="row g-2">
            <div className="col-md-6">
              <label className="form-label">Partner first name <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control"
                value={data?.partner_first_name || ''}
                onChange={(e) => update('partner_first_name', e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Partner last name <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control"
                value={data?.partner_last_name || ''}
                onChange={(e) => update('partner_last_name', e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Partner phone</label>
              <input
                type="tel"
                className="form-control"
                value={data?.partner_phone || ''}
                onChange={(e) => update('partner_phone', e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Partner email</label>
              <input
                type="email"
                className="form-control"
                value={data?.partner_email || ''}
                onChange={(e) => update('partner_email', e.target.value)}
              />
            </div>
          </div>
        )}
      </div>

      <div className="cashflow-section-card">
        <h6 className="cashflow-subsection-title">Tools &amp; Preparation</h6>
        <div className="mb-3">
          <label className="form-label">Do you use eMoney? <span className="text-danger">*</span></label>
          <div>
            {['yes', 'no'].map((opt) => (
              <div className="form-check form-check-inline" key={opt}>
                <input
                  className="form-check-input"
                  type="radio"
                  id={`emoney_${opt}`}
                  name="uses_emoney"
                  checked={data?.uses_emoney === opt}
                  onChange={() => update('uses_emoney', opt)}
                />
                <label className="form-check-label" htmlFor={`emoney_${opt}`}>
                  {opt === 'yes' ? 'Yes' : 'No'}
                </label>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label">Do you currently track your spending? <span className="text-danger">*</span></label>
          <div>
            {['yes', 'no'].map((opt) => (
              <div className="form-check form-check-inline" key={opt}>
                <input
                  className="form-check-input"
                  type="radio"
                  id={`tracks_spending_${opt}`}
                  name="tracks_spending"
                  checked={data?.tracks_spending === opt}
                  onChange={() => update('tracks_spending', opt)}
                />
                <label className="form-check-label" htmlFor={`tracks_spending_${opt}`}>
                  {opt === 'yes' ? 'Yes' : 'No'}
                </label>
              </div>
            ))}
          </div>
          {data?.tracks_spending === 'yes' && (
            <div className="mt-2">
              <label className="form-label" htmlFor="spending_method">
                What method do you use? (app, software, or self-tracking)
              </label>
              <input
                id="spending_method"
                type="text"
                className="form-control"
                value={data?.spending_method || ''}
                onChange={(e) => update('spending_method', e.target.value)}
              />
            </div>
          )}
        </div>

        <div className="mb-3">
          <label className="form-label" htmlFor="investment_check_frequency">
            How often do you check your investment portfolio? <span className="text-danger">*</span>
          </label>
          <select
            id="investment_check_frequency"
            className="form-select"
            value={data?.investment_check_frequency || ''}
            onChange={(e) => update('investment_check_frequency', e.target.value)}
          >
            <option value="">Select…</option>
            <option value="annually">Annually</option>
            <option value="quarterly">Quarterly</option>
            <option value="monthly">Monthly</option>
            <option value="weekly">Weekly</option>
            <option value="daily">Daily</option>
          </select>
        </div>

        <div className="mb-3">
          <label className="form-label d-block">
            How are you feeling about your investments? <span className="text-danger">*</span>
          </label>
          <div className="btn-group" role="group" aria-label="Investment sentiment">
            {[
              { val: 'dark_green', label: 'Great', fillCls: 'btn-dark-green',   outlineCls: 'btn-outline-dark-green' },
              { val: 'green',      label: 'Good',       fillCls: 'btn-success',       outlineCls: 'btn-outline-success' },
              { val: 'yellow',     label: 'Mixed',      fillCls: 'btn-warning',       outlineCls: 'btn-outline-warning' },
              { val: 'red',        label: 'Concerned',  fillCls: 'btn-danger',        outlineCls: 'btn-outline-danger' },
            ].map((opt) => {
              const selected = data?.investment_sentiment === opt.val;
              return (
                <button
                  key={opt.val}
                  type="button"
                  className={`btn ${selected ? opt.fillCls : opt.outlineCls}`}
                  onClick={() => update('investment_sentiment', opt.val)}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label" htmlFor="advisor_questions">
            Do you have any concerns or questions your advisor should
            prepare to address?
          </label>
          <textarea
            id="advisor_questions"
            name="advisor_questions"
            className="form-control"
            rows={3}
            autoComplete="off"
            data-lpignore="true"
            data-1p-ignore="true"
            data-form-type="other"
            value={data?.advisor_questions || ''}
            onChange={(e) => update('advisor_questions', e.target.value)}
          />
        </div>

        <div>
          <label className="form-label" htmlFor="upcoming_large_expenses">
            Do you have any known, large expenses coming up that we should
            add to your plan? (e.g. home improvement, new auto, trip
            overseas) — please include estimated dates and amounts.
          </label>
          <textarea
            id="upcoming_large_expenses"
            name="upcoming_large_expenses"
            className="form-control"
            rows={3}
            autoComplete="new-password"
            data-lpignore="true"
            data-1p-ignore="true"
            data-form-type="other"
            value={data?.upcoming_large_expenses || ''}
            onChange={(e) => update('upcoming_large_expenses', e.target.value)}
          />
        </div>
      </div>

      <NotesInput
        value={data?.notes}
        onChange={(val) => update('notes', val)}
      />
    </div>
  );
}
